/**
 * Custom hook for reCAPTCHA v3 token generation
 * Use this hook in forms that need bot protection
 */

import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

/**
 * Hook that provides reCAPTCHA token generation
 * Returns null for executeRecaptcha if reCAPTCHA is not configured
 */
export const useRecaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Execute reCAPTCHA and get a token for the specified action.
   * Wrapped in a hard 3s timeout so a slow/hung Google reCAPTCHA script
   * (e.g. "reCAPTCHA Timeout (b)" from gstatic) never blocks form saves.
   * Returns the token, or null if reCAPTCHA isn't ready / times out / errors.
   * @param {string} action - The action name (e.g., 'create_job', 'apply_job')
   * @returns {Promise<string|null>} The reCAPTCHA token or null if not available
   */
  const getRecaptchaToken = useCallback(async (action) => {
    if (!executeRecaptcha) {
      return null;
    }

    try {
      // Attach a no-op .catch so that if executeRecaptcha eventually rejects
      // AFTER our timeout has already won the race (e.g. gstatic throws
      // "reCAPTCHA Timeout (b)" 6s later), it doesn't surface as an
      // unhandled promise rejection in the console.
      const recaptchaPromise = executeRecaptcha(action).catch(() => null);
      const token = await Promise.race([
        recaptchaPromise,
        new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);
      return token || null;
    } catch (error) {
      // Defensive: any sync throw from executeRecaptcha itself.
      return null;
    }
  }, [executeRecaptcha]);

  return {
    getRecaptchaToken,
    isRecaptchaReady: !!executeRecaptcha,
  };
};

/**
 * Action names for different forms
 * Keep these consistent between frontend and backend
 */
export const RECAPTCHA_ACTIONS = {
  CREATE_JOB: 'create_job',
  APPLY_JOB: 'apply_job',
  SEND_MESSAGE: 'send_message',
  SUBMIT_REVIEW: 'submit_review',
  MONEY_TRANSFER: 'money_transfer',
  UPDATE_PROFILE: 'update_profile',
  CONTACT_FORM: 'contact_form',
  BECOME_HERO: 'become_hero',
  REFUND_REQUEST: 'refund_request',
};

export default useRecaptcha;
